import { TextField } from '@mui/material';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { validateStructureName } from '~/utils/validation';

type StructureNameFieldProps = {
    onToggleEdit: (isEditing: boolean) => void;
    roi: ROIState;
    onChange: (value: string) => void;
    label?: string;
    disabled?: boolean;
    variant?: 'standard' | 'outlined' | 'filled';
};

export default function StructureNameField({
    onToggleEdit,
    roi,
    onChange,
    label,
    disabled,
    variant = 'standard',
}: StructureNameFieldProps) {
    const [newRoiName, setNewRoiName] = useState<string>(roi.name);
    const [structureNameValidationError, setStructureNameValidationError] =
        useState<string | null>(null);
    const currentStructureSet = useRecoilValue(currentStructureSetState);

    const validate = (name: string): [boolean, string | null] => {
        if (!currentStructureSet) {
            return [false, 'No structure set'];
        }
        const structureNames = currentStructureSet.roiArray
            .map((structure) => structure.name)
            .filter((name) => name !== roi.name);
        return validateStructureName(name, structureNames);
    };

    const handleRoiNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!currentStructureSet) {
            return;
        }
        const trimmedRoiName = e.target.value.trim();
        const [isValid, validationError] = validate(trimmedRoiName);
        if (trimmedRoiName === roi.name || isValid) {
            setStructureNameValidationError(null);
        } else {
            setStructureNameValidationError(validationError);
        }

        setNewRoiName(e.target.value);
    };

    const handleRoiNameEditingEnding = () => {
        if (!currentStructureSet) {
            return;
        }
        const trimmedRoiName = newRoiName.trim();
        const [isValid] = validate(trimmedRoiName);
        if (trimmedRoiName !== roi.name && isValid) {
            onChange(trimmedRoiName);
        } else {
            // reset component state
            setNewRoiName(roi.name);
        }
        setStructureNameValidationError(null);
        onToggleEdit(false);
    };

    const handleRoiNameStopEditing = () => {
        onToggleEdit(false);
        setNewRoiName(roi.name);
        setStructureNameValidationError(null);
    };

    const handleRoiNameEditingKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (event.key === 'Enter') {
            handleRoiNameEditingEnding();
        } else if (event.key === 'Escape') {
            handleRoiNameStopEditing();
        }
    };

    return (
        <TextField
            label={label}
            type="text"
            value={newRoiName}
            onChange={handleRoiNameChange}
            onBlur={handleRoiNameEditingEnding}
            onKeyDown={handleRoiNameEditingKeyDown}
            autoFocus
            variant={variant}
            size="small"
            fullWidth
            error={!!structureNameValidationError}
            helperText={
                structureNameValidationError ||
                (roi.name === newRoiName ? '' : 'Press Enter to save')
            }
            disabled={disabled}
            data-testid="roi-name-field"
        />
    );
}
